import { FC, useContext, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import QRCode from 'qrcode.react';
import { CurrencyEnum } from '@spectrocoin/sc-currencies';
import CopyField from '../../../../components/CopyField/CopyField';
import MessageBlock, {
	MessageType,
	BlockStyle,
} from '../../../../components/MessageBlock/MessageBlock';
import TextNode, { TextStyles } from '../../../../components/TextNode/TextNode';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { getQRValue } from '../../../../helpers/currencyHelper/currencyHelper';
import { DonationStage } from '../../../../redux/DonationSlice/DonationSlice';
import styles from './Donate.module.scss';
import TestIds from '../../../../tests/TestIds';
import DonationContext from '../../Context/DonationContext';

const messages = defineMessages({
	address: {
		id: 'payments.address',
		defaultMessage: 'Address',
	},
	message: {
		id: 'payments.message',
		defaultMessage: 'Message',
	},
	destinationTag: {
		id: 'payments.destination_tag',
		defaultMessage: 'Destination tag',
	},
	memo: {
		id: 'payments.memo',
		defaultMessage: 'Memo',
	},
	depositMessageWarning: {
		id: 'payments.deposit_message_warning',
		defaultMessage:
			'The message above must be included in the payment; otherwise, the funds will be lost',
	},
	depositDestinationTagWarning: {
		id: 'payments.deposit_tag_warning',
		defaultMessage:
			'The Destination tag (if receiving wallet requires it) must be included in the payment, or your funds will be lost',
	},
	depositMemoWarning: {
		id: 'payments.deposit_memo_warning',
		defaultMessage:
			'The memo (if receiving wallet requires it) must be included in the payment, or your funds will be lost',
	},
});

const Donate: FC = () => {
	const { formatMessage } = useIntl();
	const {
		address: { data: address },
		stage,
	} = useContext(DonationContext);

	const additionalInformationLabel = useMemo(() => {
		if (!address?.depositMessage) return null;
		switch (address.payCurrencyCode) {
			case CurrencyEnum.XEM:
				return messages.message;
			case CurrencyEnum.XRP:
				return messages.destinationTag;
			case CurrencyEnum.XLM:
				return messages.memo;
			default:
				return null;
		}
	}, [address]);

	const additionalInformationWarning = useMemo(() => {
		if (!address?.depositMessage) return null;
		switch (address.payCurrencyCode) {
			case CurrencyEnum.XEM:
				return messages.depositMessageWarning;
			case CurrencyEnum.XRP:
				return messages.depositDestinationTagWarning;
			case CurrencyEnum.XLM:
				return messages.depositMemoWarning;
			default:
				return null;
		}
	}, [address]);

	if (!address || stage !== DonationStage.Donate) return null;

	return (
		<div className={styles.container} data-cy={TestIds.DonationDonate}>
			<QRCode
				data-cy={TestIds.QR}
				className={styles.qr}
				value={getQRValue(
					address.payCurrencyCode,
					address.depositAddress,
					address.depositMessage,
					null
				)}
			/>
			<div className={styles.inputs}>
				<CopyField
					copyContent={address.depositAddress}
					label={formatMessage(messages.address)}
				>
					<Tooltip content={address.depositAddress}>
						<TextNode
							className={styles.input}
							data-cy={TestIds.DonationAddress}
							style={TextStyles.Ellipsis}
						>
							{address!.depositAddress}
						</TextNode>
					</Tooltip>
				</CopyField>
				{additionalInformationLabel && (
					<CopyField
						copyContent={address.depositMessage!}
						label={formatMessage(additionalInformationLabel)}
					>
						<Tooltip content={address!.depositMessage}>
							<TextNode
								className={styles.input}
								data-cy={TestIds.DonationMessage}
								style={TextStyles.Ellipsis}
							>
								{address!.depositMessage}
							</TextNode>
						</Tooltip>
					</CopyField>
				)}
				{additionalInformationWarning && (
					<MessageBlock
						data-cy={TestIds.DonationMessageWarning}
						type={MessageType.Warning}
						style={BlockStyle.Border}
						message={formatMessage(additionalInformationWarning)}
						withIcon
					/>
				)}
			</div>
		</div>
	);
};

export default Donate;
